import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, TrendingUp, Users, Briefcase, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import JobCard from '@/components/JobCard';
import Navbar from '@/components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [recentJobs, setRecentJobs] = useState([]);
  const [stats, setStats] = useState({ total_jobs: 0, total_users: 0 });
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState({});

  useEffect(() => {
    fetchRecentJobs();
    fetchStats();
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/sections`);
      setSections(response.data.sections || {});
    } catch (error) {
      console.error('Erreur sections:', error);
    }
  };

  const fetchRecentJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs?limit=6`);
      // Backend returns { jobs, total, ... } — keep backward compat with array
      const data = Array.isArray(response.data) ? response.data : (response.data.jobs || []);
      setRecentJobs(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des emplois');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Erreur stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/jobs?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/jobs');
    }
  };

  // Get section config with defaults
  const getSection = (id) => {
    return sections[id] || { visible: true, content: {} };
  };

  const heroSection = getSection('hero');
  const categoriesSection = getSection('categories');
  const recentJobsSection = getSection('recent_jobs');
  const ctaSection = getSection('cta_signup');
  const employersSection = getSection('employers');

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner Image — contained within page width, zooms with content */}
      <div className="w-full bg-white pt-4">
        <div className="max-w-7xl mx-auto px-4" data-testid="home-hero-banner">
          <img
            src="/home-hero-banner.jpg"
            alt="Mosala.cd — Talents congolais et opportunités professionnelles"
            className="w-full h-auto block rounded-lg"
            loading="eager"
          />
        </div>
      </div>

      {/* Navbar BELOW the hero image on Home */}
      <Navbar user={user} onLogout={onLogout} />

      {/* Hero Section */}
      {heroSection.visible !== false && (
        <section className="relative bg-gradient-to-br from-sky-50 via-white to-sky-50 pt-12 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 left-10 w-64 h-64 bg-sky-100 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-sky-200 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-5xl mx-auto text-center">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6" 
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              data-testid="hero-title"
            >
              {heroSection.content?.title || "Recherchez emplois"}
            </h1>
            <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              {heroSection.content?.subtitle || "Mosala.cd connecte les talents congolais aux meilleures opportunités professionnelles. Recherchez parmi des centaines d'offres d'emploi."}
            </p>

            <form 
              onSubmit={handleSearch}
              className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto mb-12"
            >
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder={heroSection.content?.search_placeholder || "Titre de poste, mots-clés..."}
                  className="pl-12 py-6 text-lg rounded-full border-slate-200 shadow-lg"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="hero-search-input"
                />
              </div>
              <Button 
                type="submit"
                className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-6 rounded-full text-lg font-semibold shadow-lg"
                data-testid="hero-search-button"
              >
                {heroSection.content?.button_text || "Rechercher"}
              </Button>
            </form>

            <div className="flex justify-center gap-8 text-sm text-slate-600" data-testid="hero-stats">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-sky-500" />
                <span>{stats.total_jobs} Offres d'emploi</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-sky-500" />
                <span>{stats.total_users} Utilisateurs inscrits</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Categories Section */}
      {categoriesSection.visible !== false && (
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 tracking-tight text-center" 
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              data-testid="categories-title"
            >
              {categoriesSection.content?.title || "Parcourir par catégorie"}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: 'Informatique', icon: '💻', query: 'informatique' },
                { name: 'Marketing', icon: '📈', query: 'marketing' },
                { name: 'Finance', icon: '💰', query: 'finance' },
                { name: 'Santé', icon: '🏥', query: 'santé' },
                { name: 'Éducation', icon: '📚', query: 'éducation' },
                { name: 'BTP', icon: '🏗️', query: 'construction' },
                { name: 'ONG', icon: '🤝', query: 'ONG humanitarian' },
                { name: 'Autres', icon: '📋', query: '' }
              ].map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => navigate(`/jobs?search=${cat.query}`)}
                  className="p-6 bg-slate-50 hover:bg-sky-50 rounded-xl border border-slate-100 hover:border-sky-200 transition-all text-center group"
                >
                  <span className="text-3xl mb-2 block">{cat.icon}</span>
                  <span className="text-slate-700 font-medium group-hover:text-sky-700">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Jobs Section */}
      {recentJobsSection.visible !== false && (
        <section className="py-16 px-4 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <h2 
                className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" 
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                data-testid="recent-jobs-title"
              >
                {recentJobsSection.content?.title || "Offres récentes"}
              </h2>
              <Button 
                variant="ghost" 
                className="text-sky-600 hover:text-sky-700"
                onClick={() => navigate('/jobs')}
                data-testid="view-all-jobs-button"
              >
                {recentJobsSection.content?.view_all_text || "Voir tout →"}
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12">Chargement...</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentJobs.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      {ctaSection.visible !== false && (
        <section className="py-20 px-4 bg-gradient-to-br from-sky-500 to-sky-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-6" 
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              data-testid="cta-title"
            >
              {ctaSection.content?.title || "Recevez des alertes pour les emplois qui vous correspondent"}
            </h2>
            <p className="text-lg mb-8 text-sky-50">
              {ctaSection.content?.subtitle || "Inscrivez-vous gratuitement et recevez des notifications personnalisées pour les offres qui correspondent à votre profil."}
            </p>
            <Button 
              onClick={() => navigate('/auth')}
              className="bg-white text-sky-600 hover:bg-slate-50 rounded-full px-8 py-6 text-lg font-semibold shadow-xl"
              data-testid="cta-signup-button"
            >
              {ctaSection.content?.button_text || "S'inscrire gratuitement"}
            </Button>
          </div>
        </section>
      )}

      {/* Employers Section - Hidden by default */}
      {employersSection.visible === true && (
        <section className="py-20 px-4 bg-gradient-to-br from-indigo-900 to-indigo-800 text-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6" 
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                {employersSection.content?.title || "Vous recrutez ? Trouvez les meilleurs talents"}
              </h2>
              <p className="text-lg text-indigo-100 max-w-2xl mx-auto">
                {employersSection.content?.subtitle || "Publiez vos offres d'emploi et connectez-vous avec des milliers de candidats qualifiés en RDC."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Publiez gratuitement</h3>
                <p className="text-indigo-200">Créez et publiez vos offres en quelques clics</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Accédez aux talents</h3>
                <p className="text-indigo-200">Base de données de candidats qualifiés</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Suivez vos performances</h3>
                <p className="text-indigo-200">Statistiques et analytics en temps réel</p>
              </div>
            </div>

            <div className="text-center">
              <Button 
                onClick={() => navigate('/company/auth')}
                className="bg-white text-indigo-900 hover:bg-indigo-50 rounded-full px-8 py-6 text-lg font-semibold shadow-xl"
                data-testid="employer-cta-button"
              >
                {employersSection.content?.button_text || "Créer un compte employeur"}
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
